import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import { useTranslation } from "react-i18next"
import apiClient from "../../services/apiClient"
import * as adminService from "../../services/adminService"
import { useAuth } from "../../services/AuthContext"

const ROLE_OPTIONS = ["admin", "manager", "cashier", "webhook"]

type UserForm = {
    email: string
    password: string
    display_name: string
    role: string
    is_active: boolean
}

const EMPTY_FORM: UserForm = {
    email: "",
    password: "",
    display_name: "",
    role: "cashier",
    is_active: true,
}

const SuperadminAdministrationPage: React.FC = () => {
    const { t } = useTranslation()
    const { user } = useAuth() as any
    const [companies, setCompanies] = useState<any[]>([])
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>("")
    const [selectedRole, setSelectedRole] = useState<string>("admin")
    const [users, setUsers] = useState<any[]>([])
    const [error, setError] = useState<string | null>(null)

    const [openDialog, setOpenDialog] = useState(false)
    const [editingUser, setEditingUser] = useState<any | null>(null)
    const [formData, setFormData] = useState<UserForm>(EMPTY_FORM)

    const isSuperadmin = useMemo(
        () => Array.isArray(user?.roles) && user.roles.includes("superadmin"),
        [user?.roles]
    )

    const selectedCompany = useMemo(
        () =>
            companies.find((company) => company.id === selectedCompanyId) ||
            null,
        [companies, selectedCompanyId]
    )

    const loadCompanies = useCallback(async () => {
        const response = await apiClient.get("/api/companies")
        const list = response.companies || []
        setCompanies(list)
        if (!selectedCompanyId && list.length > 0) {
            setSelectedCompanyId(list[0].id)
        }
    }, [selectedCompanyId])

    const loadUsers = useCallback(async () => {
        if (!selectedCompanyId) {
            setUsers([])
            return
        }
        const rows = await adminService.getUsers(selectedCompanyId, {
            role: selectedRole,
        })
        setUsers(rows || [])
    }, [selectedCompanyId, selectedRole])

    useEffect(() => {
        ;(async () => {
            try {
                setError(null)
                await loadCompanies()
            } catch (e: any) {
                setError(e?.message || "Impossible de charger les sociétés.")
            }
        })()
    }, [loadCompanies])

    useEffect(() => {
        ;(async () => {
            try {
                setError(null)
                await loadUsers()
            } catch (e: any) {
                setError(
                    e?.message || "Impossible de charger les utilisateurs."
                )
            }
        })()
    }, [loadUsers])

    const handleOpenCreate = () => {
        setEditingUser(null)
        setFormData({ ...EMPTY_FORM, role: selectedRole })
        setOpenDialog(true)
    }

    const handleOpenEdit = (row: any) => {
        setEditingUser(row)
        setFormData({
            email: row.email,
            password: "",
            display_name: row.display_name || "",
            role:
                Array.isArray(row.roles) && row.roles[0]
                    ? row.roles[0]
                    : "cashier",
            is_active: row.is_active !== false,
        })
        setOpenDialog(true)
    }

    const handleCloseDialog = () => {
        setOpenDialog(false)
    }

    const handleSave = async () => {
        if (!selectedCompanyId) return

        try {
            setError(null)
            if (editingUser) {
                await adminService.updateUser(
                    selectedCompanyId,
                    editingUser.id,
                    {
                        display_name: formData.display_name.trim(),
                        roles: [formData.role],
                        is_active: formData.is_active,
                        ...(formData.password
                            ? { password: formData.password }
                            : {}),
                    }
                )
            } else {
                await adminService.createUser(selectedCompanyId, {
                    email: formData.email.trim(),
                    password: formData.password,
                    display_name: formData.display_name.trim(),
                    roles: [formData.role],
                })
            }
            handleCloseDialog()
            await loadUsers()
        } catch (e: any) {
            setError(e?.message || "Impossible d'enregistrer l'utilisateur.")
        }
    }

    const handleDelete = async (userId: string) => {
        if (!selectedCompanyId) return
        try {
            setError(null)
            await adminService.deleteUser(selectedCompanyId, userId)
            await loadUsers()
        } catch (e: any) {
            setError(e?.message || "Impossible de supprimer l'utilisateur.")
        }
    }

    if (!isSuperadmin) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">Accès réservé au superadmin.</Alert>
            </Container>
        )
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Typography variant="h3" fontWeight={700} sx={{ mb: 3 }}>
                {t("superadminAdministration.title")}
            </Typography>

            {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}

            <Paper
                variant="outlined"
                sx={{ p: 2, mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}
            >
                {companies.map((company) => (
                    <Chip
                        key={company.id}
                        label={company.trade_name || company.legal_name}
                        color={
                            company.id === selectedCompanyId
                                ? "primary"
                                : "default"
                        }
                        onClick={() => setSelectedCompanyId(company.id)}
                        variant={
                            company.id === selectedCompanyId
                                ? "filled"
                                : "outlined"
                        }
                    />
                ))}
            </Paper>

            {selectedCompany ? (
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    {t("superadminAdministration.entityLabel", {
                        name:
                            selectedCompany.trade_name ||
                            selectedCompany.legal_name,
                    })}
                </Typography>
            ) : null}

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "300px 1fr" },
                    gap: 2,
                }}
            >
                <Paper variant="outlined" sx={{ minHeight: 420 }}>
                    <Box
                        sx={{
                            px: 2,
                            py: 1.5,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Typography variant="subtitle2">{t("superadminAdministration.roles")}</Typography>
                    </Box>
                    <List disablePadding>
                        {ROLE_OPTIONS.map((role) => (
                            <ListItemButton
                                key={role}
                                selected={selectedRole === role}
                                onClick={() => setSelectedRole(role)}
                            >
                                <ListItemText primary={role} />
                            </ListItemButton>
                        ))}
                    </List>
                </Paper>

                <Paper variant="outlined" sx={{ minHeight: 420 }}>
                    <Box
                        sx={{
                            p: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Typography variant="subtitle2">
                            {t("superadminAdministration.usersOfRoleLabel", {
                                role: selectedRole,
                            })}
                        </Typography>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreate}
                            disabled={!selectedCompanyId}
                        >
                            {t("superadminAdministration.add")}
                        </Button>
                    </Box>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t("superadminAdministration.columns.name")}</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>{t("superadminAdministration.columns.status")}</TableCell>
                                    <TableCell align="right">{t("superadminAdministration.columns.actions")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>
                                            {row.display_name}
                                        </TableCell>
                                        <TableCell>{row.email}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={
                                                    row.is_active
                                                        ? "Actif"
                                                        : "Inactif"
                                                }
                                                color={
                                                    row.is_active
                                                        ? "success"
                                                        : "default"
                                                }
                                                size="small"
                                                variant={
                                                    row.is_active
                                                        ? "filled"
                                                        : "outlined"
                                                }
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                startIcon={<EditIcon />}
                                                onClick={() =>
                                                    handleOpenEdit(row)
                                                }
                                            >
                                                {t("superadminAdministration.edit")}
                                            </Button>
                                            <Button
                                                size="small"
                                                color="error"
                                                startIcon={<DeleteIcon />}
                                                onClick={() =>
                                                    handleDelete(row.id)
                                                }
                                            >
                                                {t("superadminAdministration.delete")}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4}>
                                            {t("superadminAdministration.emptyUsers")}
                                        </TableCell>
                                    </TableRow>
                                ) : null}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>

            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {editingUser
                        ? t("superadminAdministration.modal.editTitle")
                        : t("superadminAdministration.modal.addTitle")}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
                        <TextField
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    email: e.target.value,
                                })
                            }
                            disabled={!!editingUser}
                            required
                        />
                        <TextField
                            label={
                                editingUser
                                    ? t("superadminAdministration.modal.newPassword")
                                    : t("superadminAdministration.modal.password")
                            }
                            type="password"
                            value={formData.password}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    password: e.target.value,
                                })
                            }
                            required={!editingUser}
                        />
                        <TextField
                            label={t("superadminAdministration.modal.displayName")}
                            value={formData.display_name}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    display_name: e.target.value,
                                })
                            }
                            required
                        />
                        <TextField
                            label={t("superadminAdministration.modal.roleLabel")}
                            select
                            SelectProps={{ native: true }}
                            value={formData.role}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    role: e.target.value,
                                })
                            }
                        >
                            {ROLE_OPTIONS.map((role) => (
                                <option key={role} value={role}>
                                    {role}
                                </option>
                            ))}
                        </TextField>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            is_active: e.target.checked,
                                        })
                                    }
                                />
                            }
                            label={
                                formData.is_active
                                    ? t("superadminAdministration.modal.active")
                                    : t("superadminAdministration.modal.inactive")
                            }
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>{t("superadminAdministration.modal.cancel")}</Button>
                    <Button variant="contained" onClick={handleSave}>
                        {t("superadminAdministration.modal.save")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}

export default SuperadminAdministrationPage
